class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.container = this.querySelector(".cart-recs-container");
    this.prevBtn = this.querySelector("[data-rec-prev]");
    this.nextBtn = this.querySelector("[data-rec-next]");
    this.productIds = (this.dataset.cartProductIds || "").split(",").filter(Boolean);
    this.cartItemIds = [...this.productIds];
    try {
      this.promotedProducts = JSON.parse(this.dataset.promoted || "[]");
    } catch {
      this.promotedProducts = [];
    }
    this.prevBtn?.addEventListener("click", () => this.scroll(-1));
    this.nextBtn?.addEventListener("click", () => this.scroll(1));
    this.container.addEventListener("scroll", () => this.updateArrows(), { passive: true });
    if (this.promotedProducts.length > 0) {
      this.renderPromoted();
    } else if (this.productIds.length > 0) {
      this.fetchRecommendations();
    } else {
      this.style.display = "none";
    }
    this._boundCartUpdate = this.handleCartUpdate.bind(this);
    document.addEventListener("cart:updated", this._boundCartUpdate);
  }
  disconnectedCallback() {
    document.removeEventListener("cart:updated", this._boundCartUpdate);
    clearTimeout(this._debounceTimer);
  }
  handleCartUpdate(e) {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._doCartUpdate(e.detail), 500);
  }
  async _doCartUpdate(cart) {
    try {
      if (!cart || !cart.items) return;
      this.cartItemIds = cart.items.map((item) => String(item.product_id));
      if (this.promotedProducts.length > 0) {
        this.renderPromoted();
        return;
      }
      const newIds = cart.items.slice(0, 4).map((item) => String(item.product_id));
      if (newIds.join(",") !== this.productIds.join(",")) {
        this.productIds = newIds;
        if (this.productIds.length > 0) {
          this.style.display = "";
          this.fetchRecommendations();
        } else {
          this.style.display = "none";
        }
      }
    } catch (e) {
    }
  }
  renderPromoted() {
    const filtered = this.promotedProducts.filter(
      (p) => !this.cartItemIds.includes(String(p.id))
    );
    if (filtered.length > 0) {
      this.render(filtered.map((p) => ({
        id: p.id,
        title: p.title,
        url: p.url,
        price: p.price,
        compare_at_price: p.compare_at_price,
        available: p.available,
        featured_image: p.image,
        variants: [{ id: p.variant_id }]
      })));
      this.style.display = "";
    } else {
      this.style.display = "none";
    }
  }
  async fetchRecommendations() {
    try {
      const allProducts = [];
      for (const id of this.productIds) {
        try {
          const res = await fetch(`/recommendations/products.json?product_id=${id}&limit=2`);
          const data = await res.json();
          if (data.products) allProducts.push(...data.products);
        } catch (e) {
        }
        if (this.productIds.indexOf(id) < this.productIds.length - 1) {
          await new Promise((r) => setTimeout(r, 200));
        }
      }
      const seen = new Set(this.cartItemIds);
      const unique = [];
      for (const product of allProducts) {
        const id = String(product.id);
        if (!seen.has(id)) {
          seen.add(id);
          unique.push(product);
        }
      }
      if (unique.length > 0) {
        this.render(unique);
        this.style.display = "";
      } else {
        this.style.display = "none";
      }
    } catch (e) {
      this.style.display = "none";
    }
  }
  scroll(direction) {
    const card = this.container.querySelector(":scope > div");
    if (!card) return;
    const scrollAmount = card.offsetWidth + 12;
    this.container.scrollBy({ left: scrollAmount * direction, behavior: "smooth" });
  }
  updateArrows() {
    if (!this.prevBtn || !this.nextBtn) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.container;
    this.prevBtn.disabled = scrollLeft <= 2;
    this.nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
  }
  render(products) {
    this.container.innerHTML = products.map((product) => {
      const price = this.formatMoney(product.price);
      const comparePrice = product.compare_at_price > product.price ? this.formatMoney(product.compare_at_price) : null;
      const image = product.featured_image ? product.featured_image.replace(/(\.\w+)$/, "_200x$1") : "";
      const variantId = product.variants?.[0]?.id || "";
      return `
        <div class="flex shrink-0 items-center gap-3 rounded-xl border border-gray-border bg-white p-3" style="min-width:85%;max-width:85%">
          <a href="${product.url}" class="block h-16 w-16 shrink-0 overflow-hidden rounded-lg">
            ${image ? `<img src="${image}" alt="${this.escape(product.title)}" class="h-full w-full object-contain" loading="lazy">` : ""}
          </a>
          <div class="flex flex-1 flex-col gap-1">
            <a href="${product.url}" class="line-clamp-2 text-xs font-medium leading-snug hover:underline">
              ${this.escape(product.title)}
            </a>
            <div class="flex items-baseline gap-1">
              <span class="text-xs font-bold">${price}</span>
              ${comparePrice ? `<span class="text-[10px] text-gray-400 line-through">${comparePrice}</span>` : ""}
            </div>
          </div>
          ${product.available !== false ? `
            <button
              type="button"
              class="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary text-sm font-medium text-white transition-opacity hover:opacity-90"
              data-add-rec="${variantId}"
            >
              +
            </button>
          ` : ""}
        </div>
      `;
    }).join("");
    requestAnimationFrame(() => this.updateArrows());
    this.container.querySelectorAll("[data-add-rec]").forEach((btn) => {
      btn.addEventListener("click", (e) => this.addToCart(e, btn));
    });
  }
  async addToCart(e, btn) {
    const variantId = btn.dataset.addRec;
    if (!variantId) return;
    btn.disabled = true;
    btn.textContent = "...";
    try {
      await fetch(window.routes.cart_add_url || "/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
      });
      btn.textContent = "✓";
      const sections = "cart-drawer,cart-icon-bubble";
      const res = await fetch(`${window.routes.cart_url}?sections=${sections}`);
      const data = await res.json();
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && data["cart-drawer"]) {
        const doc = new DOMParser().parseFromString(data["cart-drawer"], "text/html");
        const newContent = doc.querySelector("#CartDrawer");
        if (newContent) {
          document.getElementById("CartDrawer").innerHTML = newContent.innerHTML;
          document.getElementById("CartDrawer-Overlay")?.addEventListener("click", () => cartDrawer.close());
        }
        cartDrawer.classList.remove("is-empty");
      }
      if (data["cart-icon-bubble"]) {
        const doc = new DOMParser().parseFromString(data["cart-icon-bubble"], "text/html");
        const newHTML = doc.querySelector(".shopify-section").innerHTML;
        document.querySelectorAll("[data-cart-bubble]").forEach((el) => {
          el.innerHTML = newHTML;
        });
      }
      document.dispatchEvent(new CustomEvent("cart:updated", { detail: data }));
    } catch (err) {
      btn.textContent = "!";
      btn.disabled = false;
    }
  }
  formatMoney(cents) {
    if (window.money_format) {
      const amount = (cents / 100).toFixed(2);
      return window.money_format.replace(/\{\{\s*amount\s*\}\}/g, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.floor(cents / 100)).replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace(".", ","));
    }
    const currency = window.Shopify?.currency?.active || "EUR";
    return new Intl.NumberFormat(window.Shopify?.locale || "en", {
      style: "currency",
      currency
    }).format(cents / 100);
  }
  escape(str) {
    const el = document.createElement("span");
    el.textContent = str;
    return el.innerHTML;
  }
}
window.customElements.define("cart-recommendations", CartRecommendations);
